import classnames from 'classnames';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

const CSS = require('./TimeInput.module.scss');

const generateHourOptions = () => {
  const hours = [];
  const limit = 12;
  for (let i = 1; i <= limit; i++) {
    hours.push(i.toString().padStart(2, '0'));
  }
  return hours;
};

const generateMinuteOptions = (step: number) => {
  const minutes: string[] = [];
  for (let minute = 0; minute < 60; minute += step) {
    minutes.push(minute.toString().padStart(2, '0'));
  }
  return minutes;
};

interface TimePicker12HoursProps {
  defaultValue: {
    hour: string;
    minute: string;
    period?: 'AM' | 'PM';
  };
  onChange?: (value: string) => void;
  disabled?: boolean;
  minuteStep?: number;
}

function TimePicker12Hours({
  defaultValue,
  onChange,
  disabled,
  minuteStep = 30,
}: TimePicker12HoursProps) {
  const [hour, setHour] = useState(defaultValue.hour);
  const [minute, setMinute] = useState(defaultValue.minute);
  const [period, setPeriod] = useState<'AM' | 'PM'>(
    defaultValue?.period || 'AM',
  );

  const hourOptions = generateHourOptions();
  const minuteOptions = useMemo(
    () => generateMinuteOptions(minuteStep),
    [minuteStep],
  );

  const emitChange = useCallback(
    (nextHour: string, nextMinute: string, nextPeriod: 'AM' | 'PM') => {
      onChange?.(`${nextHour}:${nextMinute} ${nextPeriod}`);
    },
    [onChange],
  );

  useEffect(() => {
    setHour(defaultValue.hour);
    const normalizedMinute = minuteOptions.includes(defaultValue.minute)
      ? defaultValue.minute
      : minuteOptions[0];
    setMinute(normalizedMinute);
    const nextPeriod = defaultValue?.period || 'AM';
    setPeriod(nextPeriod);
    if (normalizedMinute !== defaultValue.minute) {
      emitChange(defaultValue.hour, normalizedMinute, nextPeriod);
    }
  }, [
    defaultValue.hour,
    defaultValue.minute,
    defaultValue?.period,
    emitChange,
    minuteOptions,
  ]);

  const handleHourChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const nextHour = event.target.value;
    setHour(nextHour);
    emitChange(nextHour, minute, period);
  };

  const handleMinuteChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const nextMinute = event.target.value;
    setMinute(nextMinute);
    emitChange(hour, nextMinute, period);
  };

  const handlePeriodChange = (selectedPeriod: 'AM' | 'PM') => {
    setPeriod(selectedPeriod);
    emitChange(hour, minute, selectedPeriod);
  };

  return (
    <>
      <div className={CSS.TimeInput}>
        <select
          value={hour}
          onChange={handleHourChange}
          className={CSS.TimeSelect}
          disabled={disabled}
        >
          {hourOptions.map((h) => (
            <option key={h} value={h}>
              {h}
            </option>
          ))}
        </select>
        <span>:</span>
        <select
          value={minute}
          onChange={handleMinuteChange}
          className={CSS.TimeSelect}
          disabled={disabled}
        >
          {minuteOptions.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>
        <div className={CSS.PeriodToggle}>
          <button
            type="button"
            onClick={() => handlePeriodChange('AM')}
            className={classnames(CSS.PeriodButton, {
              [CSS.SelectedPeriodButton]: period === 'AM',
            })}
            disabled={disabled}
          >
            AM
          </button>
          <button
            type="button"
            onClick={() => handlePeriodChange('PM')}
            className={classnames(CSS.PeriodButton, {
              [CSS.SelectedPeriodButton]: period === 'PM',
            })}
            disabled={disabled}
          >
            PM
          </button>
        </div>
      </div>
      <input type="hidden" value={`${hour}:${minute} ${period}`} name="time" />
    </>
  );
}

export default TimePicker12Hours;
